/**
 * useDerwentVoice — reusable, low-latency voice hook for Hey Andi.
 * Pipeline: mic capture → VAD auto-stop → Whisper STT → (caller runs LLM) →
 * *streaming* TTS playback queue so speech starts on the first sentence.
 *
 * Perf notes:
 *  - `emitWaveform:false` skips per-frame React state (no re-render storm).
 *  - `onLevel(level)` fires a throttled 0..1 amplitude for DOM-direct visuals.
 *  - `speakChunk()/endSpeakQueue()` pipeline TTS: fetch(n+1) while play(n).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const SILENCE_THRESHOLD = 14;      // avg byte amplitude counted as silence
const SILENCE_MS = 850;            // ms of silence after speech before auto-stop
const MAX_MS = 25000;              // hard cap on a single utterance
const LEVEL_EMIT_MS = 55;          // throttle for onLevel

export const VOICE_STATE = {
  IDLE: 'idle',
  LISTENING: 'listening',
  TRANSCRIBING: 'transcribing',
  THINKING: 'thinking',
  SPEAKING: 'speaking',
};

export function useDerwentVoice({ onTranscript, onError, getBackendUrl, emitWaveform = true, onLevel, ttsVoice, ttsSpeed } = {}) {
  const { token } = useAuth();
  const backendUrl = getBackendUrl ? getBackendUrl() : process.env.REACT_APP_BACKEND_URL;

  const [state, setState] = useState(VOICE_STATE.IDLE);
  const [continuousMode, setContinuousMode] = useState(false);
  const [waveform, setWaveform] = useState(() => new Array(24).fill(0));

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(0);
  const hasSpokenRef = useRef(false);
  const lastSpeechAtRef = useRef(0);
  const continuousModeRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  const onLevelRef = useRef(onLevel);
  const ttsVoiceRef = useRef(ttsVoice);
  const ttsSpeedRef = useRef(ttsSpeed);
  const lastLevelEmitRef = useRef(0);
  const rearmTimerRef = useRef(null);

  // TTS streaming queue
  const ttsAudioRef = useRef(null);
  const speakSeqRef = useRef(0);
  const textQRef = useRef([]);
  const audioQRef = useRef([]);
  const fetchingRef = useRef(false);
  const playingRef = useRef(false);
  const speakEndedRef = useRef(false);
  const startRef = useRef(() => {});
  const maybeFinishRef = useRef(() => {});

  useEffect(() => { continuousModeRef.current = continuousMode; }, [continuousMode]);
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onLevelRef.current = onLevel; }, [onLevel]);
  useEffect(() => { ttsVoiceRef.current = ttsVoice; }, [ttsVoice]);
  useEffect(() => { ttsSpeedRef.current = ttsSpeed; }, [ttsSpeed]);

  const stopVad = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    try { audioCtxRef.current?.close(); } catch (_e) { /* ignore */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') { try { mr.stop(); } catch (_e) { /* ignore */ } }
  }, []);

  // ── TTS streaming queue ────────────────────────────────────────────
  const resetSpeak = useCallback(() => {
    speakSeqRef.current += 1;
    textQRef.current = [];
    audioQRef.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch (_e) { /* ignore */ } });
    audioQRef.current = [];
    fetchingRef.current = false;
    playingRef.current = false;
    speakEndedRef.current = false;
    if (ttsAudioRef.current) { try { ttsAudioRef.current.pause(); } catch (_e) { /* ignore */ } ttsAudioRef.current = null; }
  }, []);

  maybeFinishRef.current = (seq) => {
    if (seq !== speakSeqRef.current) return;
    if (playingRef.current || fetchingRef.current) return;
    if (textQRef.current.length || audioQRef.current.length) return;
    if (!speakEndedRef.current) return;
    setState((cur) => (cur === VOICE_STATE.SPEAKING ? VOICE_STATE.IDLE : cur));
    if (continuousModeRef.current) {
      rearmTimerRef.current = setTimeout(() => startRef.current(), 250);
    }
  };

  const playPump = useCallback((seq) => {
    if (seq !== speakSeqRef.current || playingRef.current) return;
    const url = audioQRef.current.shift();
    if (!url) { maybeFinishRef.current(seq); return; }
    playingRef.current = true;
    const a = new Audio(url);
    ttsAudioRef.current = a;
    const done = () => { try { URL.revokeObjectURL(url); } catch (_e) { /* ignore */ } if (ttsAudioRef.current === a) ttsAudioRef.current = null; playingRef.current = false; playPump(seq); };
    a.onended = done;
    a.onerror = done;
    a.play().catch(done);
  }, []);

  const fetchTts = useCallback(async (text) => {
    const fd = new FormData();
    fd.append('text', text.slice(0, 4096));
    fd.append('voice', ttsVoiceRef.current || 'nova');
    fd.append('speed', String(ttsSpeedRef.current || 1));
    const r = await fetch(`${backendUrl}/api/derwent/voice/speak`, {
      method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : undefined, body: fd,
    });
    if (!r.ok) throw new Error('tts');
    return r.blob();
  }, [backendUrl, token]);

  const fetchPump = useCallback(async (seq) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    while (textQRef.current.length) {
      if (seq !== speakSeqRef.current) break;
      const t = textQRef.current.shift();
      try {
        const blob = await fetchTts(t);
        if (seq !== speakSeqRef.current) break;
        audioQRef.current.push(URL.createObjectURL(blob));
        playPump(seq);
      } catch (_e) { /* skip chunk */ }
    }
    fetchingRef.current = false;
    maybeFinishRef.current(seq);
  }, [fetchTts, playPump]);

  const speakChunk = useCallback((text) => {
    const clean = (text || '').trim();
    if (!clean) return;
    setState((cur) => (cur === VOICE_STATE.LISTENING || cur === VOICE_STATE.TRANSCRIBING ? cur : VOICE_STATE.SPEAKING));
    speakEndedRef.current = false;
    const seq = speakSeqRef.current;
    textQRef.current.push(clean);
    fetchPump(seq);
  }, [fetchPump]);

  const endSpeakQueue = useCallback(() => {
    speakEndedRef.current = true;
    maybeFinishRef.current(speakSeqRef.current);
  }, []);

  const speak = useCallback((text, { auto = true } = {}) => {
    if (!text || !auto) return;
    resetSpeak();
    speakChunk(text);
    endSpeakQueue();
  }, [resetSpeak, speakChunk, endSpeakQueue]);

  const stopSpeaking = useCallback(() => {
    resetSpeak();
    setState((cur) => (cur === VOICE_STATE.SPEAKING ? VOICE_STATE.IDLE : cur));
  }, [resetSpeak]);

  // ── Recording ──────────────────────────────────────────────────────
  const cancel = useCallback(() => {
    hasSpokenRef.current = false;
    chunksRef.current = [];
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') { try { mr.stop(); } catch (_e) { /* ignore */ } }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    stopVad();
    setState(VOICE_STATE.IDLE);
    if (emitWaveform) setWaveform(new Array(24).fill(0));
    onLevelRef.current?.(0);
  }, [stopVad, emitWaveform]);

  const startVad = useCallback((stream) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const startedAt = Date.now();
      lastSpeechAtRef.current = startedAt;

      const tick = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        const avg = sum / data.length;
        const now = Date.now();
        if (avg > SILENCE_THRESHOLD) { hasSpokenRef.current = true; lastSpeechAtRef.current = now; }

        if (onLevelRef.current && now - lastLevelEmitRef.current > LEVEL_EMIT_MS) {
          lastLevelEmitRef.current = now;
          onLevelRef.current(Math.min(1, avg / 90));
        }
        if (emitWaveform) {
          const N = 24;
          const next = new Array(N);
          const chunk = Math.floor(data.length / N);
          for (let i = 0; i < N; i++) {
            let s = 0;
            for (let j = 0; j < chunk; j++) s += data[i * chunk + j];
            next[i] = Math.min(1, (s / chunk) / 140);
          }
          setWaveform(next);
        }

        const elapsed = now - startedAt;
        const sinceSpeech = now - lastSpeechAtRef.current;
        if (elapsed > MAX_MS) { stop(); return; }
        if (hasSpokenRef.current && sinceSpeech > SILENCE_MS) { stop(); return; }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (_e) { /* VAD optional */ }
  }, [stop, emitWaveform]);

  const transcribeAndDispatch = useCallback(async (blob) => {
    setState(VOICE_STATE.TRANSCRIBING);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'derwent.webm');
      const r = await fetch(`${backendUrl}/api/derwent/voice/transcribe`, {
        method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : undefined, body: fd,
      });
      if (!r.ok) throw new Error('Transcription failed');
      const data = await r.json();
      const text = (data.text || '').trim();
      setState(VOICE_STATE.IDLE);
      if (text && onTranscriptRef.current) onTranscriptRef.current(text, { fromVoice: true });
    } catch (e) {
      setState(VOICE_STATE.IDLE);
      if (onErrorRef.current) onErrorRef.current(e);
    }
  }, [backendUrl, token]);

  const start = useCallback(async () => {
    if (state === VOICE_STATE.LISTENING || state === VOICE_STATE.TRANSCRIBING) return;
    resetSpeak();                       // barge-in: cut any current speech
    if (rearmTimerRef.current) { clearTimeout(rearmTimerRef.current); rearmTimerRef.current = null; }
    setState(VOICE_STATE.LISTENING);
    hasSpokenRef.current = false;
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType });
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stopVad();
        onLevelRef.current?.(0);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        if (hasSpokenRef.current && blob.size > 2048) await transcribeAndDispatch(blob);
        else { setState(VOICE_STATE.IDLE); if (emitWaveform) setWaveform(new Array(24).fill(0)); }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      startVad(stream);
    } catch (_e) {
      setState(VOICE_STATE.IDLE);
      if (onErrorRef.current) onErrorRef.current(new Error('Microphone access denied or unavailable.'));
    }
  }, [state, startVad, stopVad, transcribeAndDispatch, resetSpeak, emitWaveform]);

  useEffect(() => { startRef.current = start; }, [start]);

  const setThinking = useCallback((thinking) => {
    setState((cur) => {
      if (thinking) return VOICE_STATE.THINKING;
      if (cur === VOICE_STATE.THINKING) return VOICE_STATE.IDLE;
      return cur;
    });
  }, []);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (rearmTimerRef.current) clearTimeout(rearmTimerRef.current);
    try { audioCtxRef.current?.close(); } catch (_e) { /* ignore */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (ttsAudioRef.current) { try { ttsAudioRef.current.pause(); } catch (_e) { /* ignore */ } }
  }, []);

  return {
    state,
    isListening: state === VOICE_STATE.LISTENING,
    isTranscribing: state === VOICE_STATE.TRANSCRIBING,
    isThinking: state === VOICE_STATE.THINKING,
    isSpeaking: state === VOICE_STATE.SPEAKING,
    start,
    stop,
    cancel,
    speak,
    speakChunk,
    endSpeakQueue,
    resetSpeak,
    stopSpeaking,
    setThinking,
    waveform,
    continuousMode,
    setContinuousMode,
  };
}
